import styled from 'styled-components';
import { useMemo } from 'react';
import type { IFormilySchema } from '../../types';
import { Table as LibraryTable } from '../../../components/Table/Table';
import type { ITableColumn } from '../../../components/Table/utils/types';
import { getNestedArrayTableFields } from './getNestedArrayTableFields';
import { buildTableColumns } from '../../utils/buildTableColumns';
import { normalizeColumns } from '../table/useColumnsNormilizer';
import { useExpandableNestedTables } from '../../hooks/useExpandableNestedTables';

interface INestedTablesRendererProps {
  record: Record<string, unknown>;
  childSchema?: IFormilySchema;
}

type NestedRecord = Record<string, unknown>;

const NestedTablesRenderer: React.FC<INestedTablesRendererProps> = ({
  record,
  childSchema,
}) => {
  const nestedFields = useMemo(() => {
    const fields = getNestedArrayTableFields(childSchema);
    return [...fields].sort(
      (a, b) => a.fieldDef['x-index'] - b.fieldDef['x-index']
    );
  }, [childSchema]);

  if (nestedFields.length === 0) {
    return null;
  }

  return (
    <NestedTablesContainer>
      {nestedFields.map(({ key, fieldDef }) => {
        const rawNested = record[key];
        // Hide rows flagged for deletion, matching FormArrayTable's dataSource
        // (display only — the tombstoned rows stay in the field value for submit).
        const nestedData = Array.isArray(rawNested)
          ? rawNested.filter((item: NestedRecord) => !item?.toBeDeleted)
          : [];
        const nestedChildSchema = fieldDef['x-component-props']?.childSchema;
        const title = fieldDef.title || fieldDef['x-component-props']?.title;
        const propsColumns = normalizeColumns(
          fieldDef['x-component-props']?.columns
        );

        const tableColumns = buildTableColumns<NestedRecord>(
          propsColumns,
          nestedChildSchema
        );

        return (
          <NestedTable
            key={key}
            title={title}
            data={nestedData as NestedRecord[]}
            columns={tableColumns}
            childSchema={nestedChildSchema}
            parentKey={key}
          />
        );
      })}
    </NestedTablesContainer>
  );
};

interface INestedTableProps {
  title?: string;
  data: NestedRecord[];
  columns: ITableColumn<NestedRecord>[];
  childSchema?: IFormilySchema;
  parentKey: string;
}

const NestedTable: React.FC<INestedTableProps> = ({
  title,
  data,
  columns,
  childSchema,
  parentKey,
}) => {
  const { expandable } = useExpandableNestedTables(childSchema);

  return (
    <NestedTableWrapper>
      {title && <NestedTableTitle>{title}</NestedTableTitle>}
      <LibraryTable
        data={data}
        columns={columns}
        rowKey={(rec: NestedRecord, index?: number) =>
          rec?.id ? String(rec.id) : `nested-${parentKey}-${index}`
        }
        size="small"
        expandable={expandable}
      />
    </NestedTableWrapper>
  );
};

const NestedTablesContainer = styled.div`
  padding: 16px;
  background: #fafafa;
`;

const NestedTableWrapper = styled.div`
  &:not(:last-child) {
    margin-bottom: 24px;
  }
`;

const NestedTableTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

export default NestedTablesRenderer;
